// Joining and running a LiveSwitch call.
//
// This is the one place that drives a LiveSwitch client. It is deliberately
// separate from the checklist pipeline: it produces participants and their
// video views, and it hands each remote participant's media to the bridge. From
// there the audio seam takes over and nothing downstream knows LiveSwitch
// exists (spec s3).
//
// The shared secret never comes near this file. `/liveswitch-token` in
// Longview-Api mints a 5-minute register token and tells us which identity it
// signed for; we register with exactly that.

// Type-only, so it is erased at build time and costs nothing in the bundle.
import type fmTypes from 'fm.liveswitch';
import { liveSwitchBridge } from './bridge';
import { extractRemoteAudioTrack } from './liveswitchFeed';
import { nativeVideoStream } from './media';
import { debugLog, describe as describeValue } from '../debug';
import { requestLiveSwitchGrant } from './tokens';

type Client = InstanceType<typeof fmTypes.Client>;
type Channel = InstanceType<typeof fmTypes.Channel>;
type LocalMedia = InstanceType<typeof fmTypes.LocalMedia>;
type RemoteMedia = InstanceType<typeof fmTypes.RemoteMedia>;
type ConnectionInfo = InstanceType<typeof fmTypes.ConnectionInfo>;

// The LiveSwitch SDK is ~7 MB minified - roughly forty times the rest of this
// app. Loading it up front would make a page someone opens mid-workday crawl,
// to support a feature they may never use in that tab. So it is fetched on the
// first join and cached, and the checklist stays instant either way.
let sdk: typeof fmTypes | null = null;

async function loadSdk(): Promise<typeof fmTypes> {
  if (!sdk) sdk = (await import('fm.liveswitch')).default;
  return sdk;
}

export type Participant = {
  id: string;
  label: string;
  isLocal: boolean;
  /**
   * The browser MediaStream carrying this participant's video, rendered into
   * our own <video> element.
   *
   * We deliberately do not adopt the SDK's getView() element: it sizes itself
   * against a LiveSwitch layout manager we do not use, and it is only half the
   * story anyway, since the audio element lives outside the view entirely.
   * Taking the stream and rendering it ourselves is one mechanism for both.
   */
  stream: MediaStream | null;
};

export type DeviceOption = { id: string; label: string };

export type CallEvents = {
  onParticipants?: (participants: Participant[]) => void;
  onError?: (message: string) => void;
  /** Someone else on the call asked us to switch cameras. */
  onCameraSwapRequested?: (fromLabel: string) => void;
  /** Whether this call is being recorded, once known. */
  onRecording?: (recording: boolean) => void;
};

/**
 * Channel messages are a plain string broadcast, so ours are namespaced JSON.
 * Anything unrecognised is ignored rather than assumed to be for us - the
 * channel is shared and may carry other traffic one day.
 */
const CAMERA_SWAP_REQUEST = 'vi:camera-swap-request';

/** LiveSwitch Futures are then/fail, not thenable. */
function toPromise<T>(future: { then: Function; fail: Function }): Promise<T> {
  return new Promise<T>((resolve, reject) => {
    future.then(
      (value: T) => resolve(value),
      (ex: unknown) => reject(new Error(describeException(ex))),
    );
  });
}

/**
 * LiveSwitch rejects with its own Exception type, not an Error, and not always
 * with getMessage(). Falling straight to a generic string threw away the only
 * useful information at exactly the moment it was needed, so try harder.
 */
function describeException(ex: unknown): string {
  return describeValue(ex);
}

// A downstream connection reports open before the browser has necessarily
// attached a MediaStream to its media element, so the audio track can be a
// moment behind. Poll briefly rather than register once and miss it - a
// participant whose track we never pick up is silently absent from the
// transcript, which is the worst way for this to fail.
// fm.liveswitch.ConnectionState, by value so the SDK need not be loaded to
// read them.
const CONNECTED = 4;
const FAILED = 6;
const CLOSED = 8;

const TRACK_POLL_INTERVAL_MS = 250;
const TRACK_POLL_TIMEOUT_MS = 15000;

/** Labels are only populated once permission has been granted. */
async function devicesOfKind(kind: MediaDeviceKind): Promise<DeviceOption[]> {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    return devices
      .filter((device) => device.kind === kind)
      .map((device) => ({ id: device.deviceId, label: device.label }));
  } catch {
    return [];
  }
}

export class LiveSwitchCall {
  private client: Client | null = null;
  private channel: Channel | null = null;
  private localMedia: LocalMedia | null = null;
  private upstream: { close: () => unknown } | null = null;
  private downstreams = new Map<string, { close: () => unknown }>();
  private remoteMedia = new Map<string, { label: string; media: RemoteMedia }>();
  private trackWaits = new Set<ReturnType<typeof setInterval>>();
  private joined = false;
  private fm: typeof fmTypes | null = null;
  private preferredOutputId: string | null = null;
  /** Whether this call should be recorded at all, decided in Global Config. */
  private recordingIntent = false;
  /** Whether it has been switched on, so two arrivals do not both start it. */
  private recordingStarted = false;
  /** The stream we captured at join: audio, plus the first camera. */
  private captured: MediaStream | null = null;
  /** Set once a swap replaces the video, so cleanup releases what is live. */
  private video: MediaStream | null = null;
  /** Likewise for a microphone change. */
  private audio: MediaStream | null = null;

  constructor(private readonly events: CallEvents = {}) {}

  get isJoined(): boolean {
    return this.joined;
  }

  /**
   * Mute or unmute what we send. This is local media, so it affects the call
   * only - the checklist listens to the *remote* track and is unaffected by
   * either of these (spec s4).
   */
  setAudioMuted(muted: boolean): void {
    this.localMedia?.setAudioMuted(muted);
  }

  setVideoMuted(muted: boolean): void {
    this.localMedia?.setVideoMuted(muted);
  }

  /** Cameras. Fewer than two means there is nothing to swap between. */
  async videoInputs(): Promise<DeviceOption[]> {
    return devicesOfKind('videoinput');
  }

  /**
   * Move to the other camera.
   *
   * The ordering is the whole fix. Every previous attempt opened the new camera
   * while the old one was still running, which works on desktop (two separate
   * devices) and on iOS (which multiplexes), and fails on Android, where the
   * front and back cameras are usually the same hardware and only one can be
   * open at a time. Same code, three platforms, two of them fine - which is
   * exactly the shape of the bug reported.
   *
   * So: stop the running camera first, then acquire the other one. If the
   * acquisition fails we put the original back, because a swap that leaves
   * someone with no camera at all is worse than one that does not happen.
   *
   * Returns the label switched to, or null if there was nowhere to go.
   */
  async swapCamera(): Promise<string | null> {
    if (!this.localMedia) {
      debugLog.warn('swapCamera: no localMedia');
      return null;
    }

    const currentTrack = this.videoTrack();
    const currentFacing = (
      currentTrack?.getSettings?.() as { facingMode?: string } | undefined
    )?.facingMode;
    const nextFacing = currentFacing === 'environment' ? 'user' : 'environment';
    debugLog.info(
      'swapCamera: current facing =',
      currentFacing ?? '(unknown)',
      '-> requesting',
      nextFacing,
    );

    // Release before acquiring. This is the Android constraint.
    currentTrack?.stop();
    debugLog.info('swapCamera: stopped current camera');

    let stream: MediaStream;
    try {
      stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: { exact: nextFacing } },
      });
    } catch (err) {
      debugLog.error('swapCamera: could not open', nextFacing, describeValue(err));
      await this.restoreCamera(currentFacing);
      throw err instanceof Error ? err : new Error(describeValue(err));
    }

    const track = stream.getVideoTracks()[0];
    const label = nextFacing === 'environment' ? 'Back camera' : 'Front camera';
    debugLog.info('swapCamera: opened', track?.label ?? label);

    try {
      await toPromise(
        this.localMedia.changeVideoMediaStream(
          stream,
          track?.getSettings?.().deviceId ?? nextFacing,
          label,
          true,
        ) as never,
      );
    } catch (err) {
      debugLog.error('swapCamera: changeVideoMediaStream REJECTED', describeValue(err));
      stream.getTracks().forEach((t) => t.stop());
      await this.restoreCamera(currentFacing);
      throw err;
    }

    this.video = stream;
    this.publish();
    debugLog.info('swapCamera: switched to', label);
    return label;
  }

  /** Best effort: get some camera back after a failed swap. */
  private async restoreCamera(facingMode: string | undefined): Promise<void> {
    if (!this.localMedia) return;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: facingMode ? { facingMode: { ideal: facingMode } } : true,
      });
      await toPromise(
        this.localMedia.changeVideoMediaStream(stream, 'restored', 'Camera', true) as never,
      );
      this.video = stream;
      this.publish();
      debugLog.info('swapCamera: restored the previous camera');
    } catch (err) {
      debugLog.error('swapCamera: could not restore a camera', describeValue(err));
    }
  }

  /** The video track currently running, wherever it came from. */
  private videoTrack(): MediaStreamTrack | null {
    return (this.video ?? this.captured)?.getVideoTracks()[0] ?? null;
  }

  /**
   * Hand our current microphone track to the audio feed.
   *
   * Called again after a microphone change, because the track object is
   * replaced and the old one stops producing.
   */
  private publishLocalAudio(): void {
    const track = (this.audio ?? this.captured)?.getAudioTracks()[0];
    if (!track) {
      debugLog.warn('local audio: no microphone track to publish');
      return;
    }
    debugLog.info('local audio: published', track.label || '(unlabelled)');
    liveSwitchBridge.registerLocalAudio(track);
  }

  /**
   * Start recording, the first time there is someone to record.
   *
   * Both sides run this, whichever notices the other first, and it is harmless
   * for both to try. The flag is set before the await so two participants
   * arriving together cannot start it twice, and cleared again on failure so a
   * later arrival gets another go.
   */
  private async startRecordingOnce(): Promise<void> {
    if (!this.recordingIntent || this.recordingStarted) return;
    const fm = this.fm;
    const channel = this.channel;
    if (!fm || !channel) return;

    this.recordingStarted = true;
    try {
      await this.enableRecording(fm, channel);
      this.events.onRecording?.(true);
    } catch {
      this.recordingStarted = false;
    }
  }

  /**
   * Ask LiveSwitch to record this channel.
   *
   * Recording happens entirely on their servers: every participant's audio and
   * video is mixed into one file after the session, with nothing captured in
   * the browser and nothing uploaded from a phone. The only documented way to
   * switch it on is a client-side channel update, which is why this lives here
   * and why the token carries a canUpdate claim.
   *
   * A failure here must not stop the call. Someone joining to show us a flooded
   * basement should not be turned away because a recording could not start -
   * but it is loud in the log, because a call that silently is not recorded is
   * worse than one that obviously is not.
   */
  private async enableRecording(fm: typeof fmTypes, channel: Channel): Promise<void> {
    try {
      const config = new fm.ChannelConfig();
      config.setRecording(true);
      await toPromise((channel as unknown as { update(c: unknown): unknown }).update(config) as never);
      debugLog.info('recording: enabled for this channel');
    } catch (err) {
      debugLog.error('recording: could not enable', describeValue(err));
      this.events.onError?.('This call may not be recorded.');
      throw err;
    }
  }

  /** Ask the other side to switch cameras. Advisory - they choose. */
  requestCameraSwap(): void {
    const channel = this.channel as unknown as { sendMessage?: (m: string) => unknown } | null;
    channel?.sendMessage?.(JSON.stringify({ t: CAMERA_SWAP_REQUEST }));
  }

  /** Microphones, once permission has been granted and labels are populated. */
  async audioInputs(): Promise<DeviceOption[]> {
    return devicesOfKind('audioinput');
  }

  /** Switch microphone. SDK-owned route, matching the camera. */
  async setAudioInput(deviceId: string): Promise<void> {
    if (!this.localMedia) return;
    // Caller-owned media means changeAudioMediaStream, matching the camera.
    // Microphones do not have the one-at-a-time constraint cameras do on
    // Android, so this acquires before releasing.
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: {
        deviceId: { exact: deviceId },
        echoCancellation: true,
        noiseSuppression: true,
        autoGainControl: true,
      },
    });
    try {
      await toPromise(
        this.localMedia.changeAudioMediaStream(stream, deviceId, 'Microphone', true) as never,
      );
      this.audio?.getTracks().forEach((track) => track.stop());
      this.audio = stream;
      debugLog.info('setAudioInput: switched microphone');
      this.publishLocalAudio();
    } catch (err) {
      stream.getTracks().forEach((track) => track.stop());
      debugLog.error('setAudioInput failed', describeValue(err));
      throw err;
    }
  }

  /**
   * Speakers. Read off a remote media, because output is a property of where
   * audio is played, not where it is captured. Empty on browsers without
   * setSinkId (Firefox, iOS Safari), which is a signal to hide the control
   * rather than an error.
   */
  async audioOutputs(): Promise<DeviceOption[]> {
    const first = [...this.remoteMedia.values()][0]?.media;
    if (!first) return [];
    const outputs = await toPromise<Array<{ getId(): string; getName(): string }>>(
      (first as unknown as { getAudioSinkOutputs(): unknown }).getAudioSinkOutputs() as never,
    ).catch(() => []);
    return outputs.map((output) => ({ id: output.getId(), label: output.getName() }));
  }

  /**
   * Output is per-RemoteMedia, so this applies to everyone currently in the
   * call and is remembered for anyone who joins later - otherwise the next
   * arrival would come out of the old speaker.
   */
  async setAudioOutput(deviceId: string): Promise<void> {
    this.preferredOutputId = deviceId;
    await Promise.all(
      [...this.remoteMedia.values()].map((entry) => this.applyOutput(entry.media)),
    );
  }

  private async applyOutput(media: RemoteMedia): Promise<void> {
    if (!this.preferredOutputId) return;
    const sinkable = media as unknown as {
      getAudioSinkOutputs(): unknown;
      changeAudioSinkOutput(output: unknown): unknown;
    };
    try {
      const outputs = await toPromise<Array<{ getId(): string }>>(
        sinkable.getAudioSinkOutputs() as never,
      );
      const chosen = outputs.find((output) => output.getId() === this.preferredOutputId);
      if (chosen) await toPromise(sinkable.changeAudioSinkOutput(chosen) as never);
    } catch {
      // No setSinkId support, or the device vanished. The default output still
      // works, so this is not worth failing a call over.
    }
  }

  async join(channelId: string, displayName: string): Promise<void> {
    if (this.joined) return;

    const [fm, grant] = await Promise.all([
      loadSdk(),
      requestLiveSwitchGrant(channelId, displayName),
    ]);

    // The token is signed for exactly these ids. Constructing the client with
    // anything else fails registration.
    const client = new fm.Client(
      grant.gatewayUrl,
      grant.applicationId,
      grant.userId,
      grant.deviceId,
      grant.clientId,
    );
    this.client = client;
    this.fm = fm;

    const channels = await toPromise<Channel[]>(client.register(grant.token) as never);
    debugLog.info('join: registered on', String(channels.length), 'channel(s)');
    const channel = channels[0];
    if (!channel) throw new Error('LiveSwitch registered but returned no channel');
    this.channel = channel;

    // Recording is not started here. It starts when someone else actually
    // joins - see startRecordingOnce - so an inspector waiting alone for a
    // customer to pick up is not filed away as several minutes of an empty
    // room. LiveSwitch supports switching it on mid-session precisely so the
    // channel does not have to be restarted for it.
    this.recordingIntent = grant.recording;

    // Local camera and mic, captured by us.
    //
    // We need to own the stream because we need to control the ORDER in which
    // cameras are opened and closed - see swapCamera. Owning it also lets us
    // state the audio constraints, echo cancellation included.
    const captured = await navigator.mediaDevices.getUserMedia({
      audio: { echoCancellation: true, noiseSuppression: true, autoGainControl: true },
      video: { facingMode: { ideal: 'user' } },
    });
    this.captured = captured;
    debugLog.info(
      'join: captured',
      captured.getVideoTracks()[0]?.label ?? 'no video',
      `facing=${
        (captured.getVideoTracks()[0]?.getSettings?.() as { facingMode?: string } | undefined)
          ?.facingMode ?? '?'
      }`,
    );

    const localMedia = new fm.LocalMedia(captured);
    this.localMedia = localMedia;
    await toPromise(localMedia.start() as never);

    // Our own half of the conversation goes to the checklist too.
    //
    // Spec s4 starts remote-only and says why: the far side usually repeats
    // what matters, and local audio brings echo with it. In practice the
    // inspector states plenty the customer never repeats - and an inspector
    // testing alone hears nothing transcribed at all, which reads as a broken
    // pipeline. The spec anticipated this exact reversal and called it a
    // one-line change; this is that line.
    this.publishLocalAudio();

    const upstream = channel.createSfuUpstreamConnection(
      new fm.AudioStream(localMedia),
      new fm.VideoStream(localMedia),
    );
    upstream.open();
    this.upstream = upstream as unknown as { close: () => unknown };

    channel.addOnRemoteUpstreamConnectionOpen((info: ConnectionInfo) => {
      debugLog.info('remote upstream opened:', info.getClientId(), info.getUserId() ?? '');
      this.openDownstream(info);
    });

    // addOnRemoteUpstreamConnectionOpen only fires for upstreams that open
    // AFTER this subscription, so whoever is already in the channel would never
    // be seen or heard by the person joining second. Sweep the existing ones.
    // Subscribing first and sweeping second means a connection opening during
    // the sweep is picked up either way; openDownstream ignores duplicates.
    // How many were already here is the whole question when somebody joins a
    // call that is already running. Logged rather than inferred: an empty sweep
    // and a sweep that found somebody look identical from the outside.
    const existing = channel.getRemoteUpstreamConnectionInfos();
    debugLog.info('join: sweep found', String(existing.length), 'existing upstream(s)');
    for (const info of existing) {
      debugLog.info('join: sweeping', info.getClientId(), info.getUserId() ?? '');
      this.openDownstream(info);
    }
    channel.addOnRemoteClientLeave((info: { getId?: () => string }) => {
      const id = info?.getId?.();
      if (id) this.dropRemote(id);
    });

    channel.addOnMessage((from: { getUserId?: () => string }, message: string) => {
      let parsed: { t?: string };
      try {
        parsed = JSON.parse(message);
      } catch {
        return; // not ours
      }
      if (parsed?.t === CAMERA_SWAP_REQUEST) {
        this.events.onCameraSwapRequested?.(from?.getUserId?.() || 'Your inspector');
      }
    });

    this.joined = true;
    this.publish();
  }

  private openDownstream(info: ConnectionInfo): void {
    const channel = this.channel;
    const fm = this.fm;
    if (!channel || !fm) return;

    const clientId = info.getClientId();
    if (this.downstreams.has(clientId)) {
      debugLog.info('downstream: already open for', clientId);
      return;
    }

    try {
      const remoteMedia = new fm.RemoteMedia();
      const downstream = channel.createSfuDownstreamConnection(
        info,
        new fm.AudioStream(remoteMedia),
        new fm.VideoStream(remoteMedia),
      );
      downstream.open();

      debugLog.info('downstream: opened for', clientId);
      this.downstreams.set(clientId, downstream as unknown as { close: () => unknown });
      this.remoteMedia.set(clientId, { label: info.getUserId() || clientId, media: remoteMedia });
      this.publish();

      // The stream is not attached until the connection is actually carrying
      // media, so the publish above usually has none to show. Publish again on
      // Connected, when there is.
      const connection = downstream as unknown as {
        addOnStateChange?: (fn: (conn: { getState?: () => number }) => void) => void;
      };
      connection.addOnStateChange?.((conn) => {
        const state = conn.getState?.();
        if (state === CONNECTED) this.publish();
        else if (state === FAILED || state === CLOSED) this.dropRemote(clientId);
      });

      // Whoever joins later comes out of the speaker already chosen, rather
       // than reverting to the system default.
      void this.applyOutput(remoteMedia);

      // Somebody else is here, so there is now a conversation worth recording.
      void this.startRecordingOnce();

      // The hook. From here the checklist pipeline has this participant's audio
      // and this file's involvement ends.
      this.registerWhenAudible(clientId, remoteMedia);
    } catch (err) {
      // Silent until now. A downstream that fails to open is exactly the
      // "nobody joined" symptom, seen from the inside.
      debugLog.error('downstream: could not open for', clientId, describeValue(err));
      this.events.onError?.(err instanceof Error ? err.message : String(err));
    }
  }

  private registerWhenAudible(clientId: string, remoteMedia: RemoteMedia): void {
    const startedAt = Date.now();
    const timer = setInterval(() => {
      if (!this.downstreams.has(clientId)) {
        clearInterval(timer);
        this.trackWaits.delete(timer);
        return;
      }
      if (extractRemoteAudioTrack(remoteMedia)) {
        clearInterval(timer);
        this.trackWaits.delete(timer);
        liveSwitchBridge.registerRemoteMedia(clientId, remoteMedia);
        return;
      }
      if (Date.now() - startedAt > TRACK_POLL_TIMEOUT_MS) {
        clearInterval(timer);
        this.trackWaits.delete(timer);
        this.events.onError?.(
          `No audio track appeared for ${clientId}; they will not be transcribed.`,
        );
      }
    }, TRACK_POLL_INTERVAL_MS);
    this.trackWaits.add(timer);
  }

  private dropRemote(clientId: string): void {
    const downstream = this.downstreams.get(clientId);
    if (downstream) {
      try {
        downstream.close();
      } catch {
        /* already gone */
      }
    }
    this.downstreams.delete(clientId);
    this.remoteMedia.delete(clientId);
    liveSwitchBridge.unregisterRemoteMedia(clientId);
    this.publish();
  }

  private publish(): void {
    const participants: Participant[] = [];
    if (this.localMedia) {
      participants.push({
        id: 'local',
        label: 'You',
        isLocal: true,
        stream: nativeVideoStream(this.localMedia),
      });
    }
    for (const [id, entry] of this.remoteMedia) {
      participants.push({
        id,
        label: entry.label,
        isLocal: false,
        stream: nativeVideoStream(entry.media),
      });
    }
    this.events.onParticipants?.(participants);
  }

  async leave(): Promise<void> {
    for (const timer of this.trackWaits) clearInterval(timer);
    this.trackWaits.clear();

    for (const clientId of [...this.downstreams.keys()]) this.dropRemote(clientId);

    try {
      this.upstream?.close();
    } catch {
      /* already closed */
    }
    this.upstream = null;

    if (this.localMedia) {
      // Releases the camera and microphone. Skipping this leaves the camera
      // light on after the call ends.
      await toPromise(this.localMedia.stop() as never).catch(() => undefined);
      this.localMedia = null;
    }

    // We opened these, so we close them. The SDK will not release media it did
    // not acquire, and a camera left running is a light left on.
    liveSwitchBridge.unregisterLocalAudio();

    for (const stream of [this.captured, this.video, this.audio]) {
      stream?.getTracks().forEach((track) => track.stop());
    }
    this.captured = null;
    this.video = null;
    this.audio = null;

    if (this.client) {
      await toPromise(this.client.unregister() as never).catch(() => undefined);
      this.client = null;
    }

    this.channel = null;
    this.fm = null;
    this.recordingIntent = false;
    this.recordingStarted = false;
    this.remoteMedia.clear();
    this.joined = false;
    this.publish();
  }
}
